import { Action, Reducer } from 'redux';
import { REHYDRATE } from 'redux-persist';
import { set } from 'lodash/fp';

import {
  BUILD_YOUR_COVER_INVALID_PRODUCT_TYPES,
  BUILD_YOUR_COVER_RESET_PRODUCT,
  BUILD_YOUR_COVER_SELECT_PRODUCT,
  BUILD_YOUR_COVER_SERVICES_CHANGE,
  BUILD_YOUR_COVER_SET_ADD_ON_TYPE,
  BUILD_YOUR_COVER_SKIP_STEP,
  BUILD_YOUR_COVER_STEP_CHANGE,
} from '../../actions/action-types';
import { BuildYourCoverProductType } from '../../actions/build-your-cover';
import { ProductType, ProductTypeType } from '../../types/product';
import { Service } from '../../types/__codegen__/ahm-api/graphql';

type SelectServices = Action & {
  productType: BuildYourCoverProductType;
  selectedServices: Service['serviceId'][];
};

type SelectProduct = Action & {
  productType: BuildYourCoverProductType;
  selectedProductId: ProductType['id'];
};

type SetInvalidProductTypes = Action & {
  invalidProductTypes: BuildYourCoverProductType[];
};

type SkipStep = Action & {
  serviceType: BuildYourCoverProductType;
};

type ChangeStep = Action & {
  activeStep: string;
};

type SetAddOnType = Action & {
  addOnType: BuildYourCoverProductType | null;
};

type Rehydrate = Action & {
  key: 'buildYourCover';
  payload: object;
};

export type ActionType =
  | SelectServices
  | ChangeStep
  | SelectProduct
  | SetInvalidProductTypes
  | SkipStep
  | SetAddOnType
  | Rehydrate;

export type BuildYourCoverStateType = {
  activeStep: string | null;
  addOnType: BuildYourCoverProductType | null;
  invalidProductTypes: ProductTypeType[];
  selectedProducts: {
    extras: ProductType['id'] | null;
    hospital: ProductType['id'] | null;
  };
  selectedServices: {
    extras: Service['serviceId'][];
    hospital: Service['serviceId'][];
  };
  skippedSteps: {
    extras: boolean;
    hospital: boolean;
  };
};

const initialState = {
  activeStep: null,
  addOnType: null,
  invalidProductTypes: [],
  selectedProducts: {
    extras: null,
    hospital: null,
  },
  selectedServices: {
    extras: [],
    hospital: [],
  },
  skippedSteps: {
    extras: false,
    hospital: false,
  },
} as BuildYourCoverStateType;

const buildYourCoverReducer: Reducer<BuildYourCoverStateType, ActionType> = (
  // eslint-disable-next-line default-param-last -- this is a conflict between eslint and redux
  state = initialState,
  action,
): BuildYourCoverStateType => {
  const reducers = {
    [BUILD_YOUR_COVER_INVALID_PRODUCT_TYPES]: () => {
      const { invalidProductTypes } = action as SetInvalidProductTypes;
      return set('invalidProductTypes', invalidProductTypes, state);
    },

    [BUILD_YOUR_COVER_RESET_PRODUCT]: () => {
      const { productType } = action as SelectProduct;
      return set(['selectedProducts', productType], initialState.selectedProducts[productType], state);
    },

    [BUILD_YOUR_COVER_SELECT_PRODUCT]: () => {
      const { productType, selectedProductId } = action as SelectProduct;
      return set(['selectedProducts', productType], selectedProductId, state);
    },

    [BUILD_YOUR_COVER_SERVICES_CHANGE]: () => {
      const { productType, selectedServices } = action as SelectServices;
      const withServices = set(['selectedServices', productType], selectedServices, state);
      return set(['skippedSteps', productType], false, withServices);
    },

    [BUILD_YOUR_COVER_SET_ADD_ON_TYPE]: () => ({
      ...state,
      addOnType: (action as SetAddOnType).addOnType,
    }),

    [BUILD_YOUR_COVER_SKIP_STEP]: () => {
      const { serviceType } = action as SkipStep;
      return set(['skippedSteps', serviceType], true, state);
    },

    [BUILD_YOUR_COVER_STEP_CHANGE]: () => ({
      ...state,
      activeStep: (action as ChangeStep).activeStep,
    }),

    [REHYDRATE]: () => {
      const { key, payload } = action as Rehydrate;
      if (key === 'buildYourCover') {
        return { ...state, ...payload };
      }

      return state;
    },
  };

  const reducer = reducers?.[action.type as keyof typeof reducers];
  return reducer ? reducer() : state;
};

export default buildYourCoverReducer;
